/**
 * 계약서 기본값. 조직 설정(`ORG_SETTING_DEFAULT`)이 이 값을 문자열로 굳혀 두고,
 * 저장소 `create`(묶음 5)가 설정에서 복사해 계약 행에 넣는다.
 *
 * **기본값이 완벽할 필요는 없다** — 회사별 편집을 허용하기로 했다(메모리
 * 「확정 사항」). 다만 **기본값이 법에서 깎이는 값이면 안 된다.**
 */

import {
  ClauseSource,
  PaymentPlanRow,
  WarrantyTerm,
} from "./types";

/**
 * 기본 조항 출처. 소비자 상대 인테리어 전용 서식이 기본이다.
 *
 * **`clauses.ts` 가 아니라 여기 있다** — 조직 설정(`org/types.ts`)이 이 값을 쓰는데,
 * `clauses.ts` 에서 가져오면 `org/types` 를 import 하는 24개 파일(클라이언트
 * 컴포넌트 포함)이 조항 원문 수십 KB 를 통째로 번들에 끌고 간다.
 */
export const DEFAULT_CLAUSE_SOURCE: ClauseSource = "FTC_10079";

// 지체상금률 5bp = 1천분의 0.5 — 국가계약법 시행규칙 제75조 공사 요율이고, 국토부 제30조 ④ 가 요율을 안 정하면 이 값을 따르게 한다.
export const DEFAULT_DELAY_RATE_BP = 5;

/**
 * 지체상금 한도 1000bp = 도급금액의 10%. 한도 없이 쌓이는 조항은 재판에서 깎인다.
 *
 * ⚠️ **한도는 어느 원문에도 없다.** 국토부 제30조는 상한을 두지 않고, 공정위는
 * 지체상금 조항 자체가 없다. 본문은 무한인데 계산만 10% 에서 멈추면 **문서와 값이
 * 갈린다** — 한도를 실제로 쓰려면 특약(`special_terms`)으로 본문에 올려야 한다(묶음 4b).
 */
export const DEFAULT_DELAY_CAP_BP = 1_000;

/** 하자담보 12개월. 방수·창호처럼 법령이 더 긴 공종은 조항 본문이 받는다. */
export const DEFAULT_WARRANTY_MONTHS = 12;

/**
 * 연체이율 기본 0 = **「안 정했다」**. 원문도 빈칸(`(     )%`)이다.
 *
 * 우리가 숫자를 정하지 않는다 — 「시중은행 일반자금 대출 연체이율 범위 내」 라는
 * 단서가 은행·시기마다 다르고, 높게 심으면 소비자에게 불리한 기본값이 된다.
 */
export const DEFAULT_LATE_INTEREST_RATE_BP = 0;

/** 연 이율을 일할로 나눌 때 쓰는 날수. 윤년을 따지지 않는다 — 계약서 관행이다. */
export const DAYS_PER_YEAR = 365;

/**
 * 착수 전 해제 위약금률의 **상한** 1000bp = 10%.
 *
 * 우리가 고른 값이 아니라 **공정위 제7조 ③ 1. 본문에 박힌 값**이다
 * (「단 총 공사금액의 10%를 초과할 수 없음」). 넘겨 저장하면 표준서식을 달고
 * 무효인 계약서가 나간다.
 */
export const PENALTY_RATE_CAP_BP = 1_000;

/**
 * 위약금률 기본 0 = **「안 정했다」**. 원문도 빈칸(`(     )%`)이다.
 *
 * 우리가 숫자를 정해 두지 않는다 — 소비자에게 불리한 쪽으로 기본값을 심는 일이
 * 되고, 회사마다 다르다. 계약 화면이 받는다.
 */
export const DEFAULT_PENALTY_RATE_BP = 0;

/**
 * 공종별 하자담보 예외. **공정위 제10조 ① 각 호 그대로**다 — 실내건축 1년(일반
 * `DEFAULT_WARRANTY_MONTHS`)이고 창호만 2년, 그중 유리는 1년이다.
 */
export const DEFAULT_WARRANTY_TERMS: readonly WarrantyTerm[] = [
  // **공종명(`template/trades.ts`)과 같은 말이어야 한다.** 「창호」 로 두었더니
  // 실제 공종 「창호 공사」 로 조회했을 때 예외를 못 찾고 일반 12개월이 나왔다
  // — 샷시 현장에서 본문 2년·표지 1년이 갈리는 자리다(2026-09-22 리뷰).
  { label: "창호 공사", months: 24 },
  // 유리는 공종이 아니라 **창호 안의 예외**다(제10조 ① 2호 괄호).
  { label: "유리", months: 12 },
];

/**
 * 공정위 제10조 ① 이 정한 **최소** 기간. 회사가 이보다 짧게 적으면 그 조항은
 * 약관법상 무효인데, 본문은 축자 제10조라 **문서와 표지가 갈린다.**
 *
 * **국토부에는 이 수가 없다.** 제31조는 기간을 계약서에 따로 적게 하고 건산법
 * 시행령 별표를 따르므로, 여기 값을 국토부 계약에 들이대면 **본문에 없는 수를
 * 인용하며 저장을 막는다**(2026-09-22 재리뷰).
 */
export const FTC_MIN_WARRANTY_MONTHS = {
  /** 제10조 ① 1호 실내건축 공사 1년. **예외 목록이 아니라 일반 기간에 걸린다.** */
  general: 12,
  /**
   * 제10조 ① 2호. 키는 **공종명(`template/trades.ts`)과 같은 말**이어야 한다.
   *
   * 회사가 공종을 추가할 수 있어 컴파일 타임 유니온으로는 못 닫는다 —
   * 대신 `tests/contract-calc.test.ts` 가 공종 목록·원문 낱말과 대조한다.
   */
  byLabel: {
    "창호 공사": 24,
    유리: 12,
  } as Record<string, number>,
} as const;

/**
 * 공종 이름을 견줄 때 쓰는 꼴. **공백을 지운다** — 「창호 공사」와 「창호공사」는
 * 같은 말인데, 그대로 비교하면 24개월을 적어 놓고 「24개월보다 짧다」 며 막힌다
 * (실측 · 2026-09-22 재리뷰). 「창호」·「샷시」처럼 말 자체가 다른 것까지는 못 본다.
 */
export const warrantyLabelKey = (label: string): string =>
  label.replace(/\s+/g, "");

/** 하자보수 보증금 0%. 소규모 인테리어에서 실제로 떼는 곳이 드물다. */
export const DEFAULT_WARRANTY_DEPOSIT_BP = 0;

/**
 * 기본 지급회차 3회. 합 10000 은 `assertPaymentRatios` 가 본다.
 *
 * `trigger_text` 가 필수인 이유 — 필로스 실물이 「도기세팅 **또는** 타일 완료」라
 * 공정 id 하나로는 못 담는다(노트 `06` 5절).
 */
export const DEFAULT_PAYMENT_PLAN: readonly PaymentPlanRow[] = [
  { seq: 1, label: "계약금", ratio_bp: 3_000, trigger_text: "계약 체결 시" },
  { seq: 2, label: "중도금", ratio_bp: 4_000, trigger_text: "목공사 완료 시" },
  { seq: 3, label: "잔금", ratio_bp: 3_000, trigger_text: "준공·인도 시" },
];
